import { useQuery } from "@tanstack/react-query";
import { Building2, LogIn, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage, unwrap } from "../../api/client";
import { useOrg } from "../../api/org";
import { Alert, Badge, Button, Card, Empty, PageHead, Spinner, Switch, useToast } from "../../components/ui";
import { StatusBadge } from "../../components/StatusBadge";
import { initials } from "../../lib/format";
import { useT } from "../../i18n/I18nProvider";
import { DirIcon } from "../../i18n/mirror";
import { useFmt } from "./format";

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** "Open company": the company's dashboard without a membership (audited). */
export function useOpenCompany() {
  const { openAsAdmin } = useOrg();
  const navigate = useNavigate();
  const toast = useToast();
  return async (id: string) => {
    try {
      await openAsAdmin(id);
      navigate("/designs");
    } catch (e) {
      toast(errorMessage(e), true);
    }
  };
}

export function AdminOrgs() {
  const navigate = useNavigate();
  const open = useOpenCompany();
  const t = useT();
  const { lux, date } = useFmt();
  const [q, setQ] = useState("");
  const [charter, setCharter] = useState(false);
  const query = useDebounced(q.trim());
  const orgs = useQuery({
    queryKey: ["admin-orgs", query, charter],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs", { params: { query: { q: query || undefined, limit: 100, badge: charter ? "charter" : undefined } } })),
    placeholderData: (prev) => prev,
  });
  return (
    <>
      <PageHead title={t("admin.orgs.title")} description={t("admin.orgs.description")} />
      <Card pad={false}>
        <div className="toolbar">
          <div className="input-icon">
            <Search />
            <input className="input" placeholder={t("admin.orgs.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          </div>
          <Switch checked={charter} onChange={setCharter} label={t("admin.orgs.charterOnly")} />
          {orgs.data && <span className="small faint">{t("admin.orgs.shown", { count: orgs.data.items.length })}</span>}
        </div>
        {orgs.isLoading ? (
          <Spinner />
        ) : orgs.error ? (
          <div className="card-body">
            <Alert tone="danger">{errorMessage(orgs.error)}</Alert>
          </div>
        ) : !orgs.data?.items.length ? (
          <Empty icon={<Building2 />} title={t("admin.orgs.empty")} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("admin.orgs.colOrganisation")}</th>
                  <th>{t("admin.orgs.colStatus")}</th>
                  <th className="hide-sm">{t("admin.orgs.colPlan")}</th>
                  <th className="hide-sm">{t("admin.orgs.colCountry")}</th>
                  <th className="num">Lux</th>
                  <th className="num hide-sm">{t("admin.orgs.colCreated")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orgs.data.items.map(({ organization: o, balance }) => (
                  <tr key={o.id} className="clickable" onClick={() => navigate(`/admin/orgs/${o.id}`)}>
                    <td>
                      <div className="row" style={{ flexWrap: "nowrap" }}>
                        <div className="org-avatar">{initials(o.name)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div className="cell-main ellipsis">
                            <span dir="auto">{o.name}</span> <StatusBadge badge={o.badge} size="sm" />
                          </div>
                          <div className="cell-sub mono" dir="auto">{o.slug || o.id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge tone={o.status === "active" ? "ok" : "danger"}>{t("admin.orgs.status", { status: o.status })}</Badge>
                    </td>
                    <td className="hide-sm">{t("admin.orgs.planName", { plan: o.plan || "free" })}</td>
                    <td className="hide-sm" dir="auto">{o.country || "—"}</td>
                    <td className="num" style={{ fontWeight: 600, color: balance <= 0 ? "var(--danger)" : undefined }}>
                      {lux(balance)}
                    </td>
                    <td className="num hide-sm">{date(o.created_at)}</td>
                    <td className="right">
                      <Button size="sm" variant="ghost" icon={<DirIcon><LogIn /></DirIcon>} onClick={(e) => { e.stopPropagation(); void open(o.id); }} title={t("admin.orgs.openTitle")}>
                        {t("admin.orgs.open")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
